import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import { supabase } from '../../supabase';
import { ModalCapturaMuestreo } from './ModalCapturaMuestreo';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import {
  ClipboardCheck, RefreshCw, Loader2, CheckCircle2, RotateCcw, WifiOff, CloudUpload,
} from 'lucide-react';

// ── Tipos ────────────────────────────────────────────────────────────────────
interface ParResultado { clave: string; valor: string; }
interface Muestreo {
  id: number;
  labor_nombre: string;
  grupo_forza: string | null;
  lote_num: string | null;
  fecha_programada: string;
  semana: string;
  estado: string;
  tipo_muestreo: string | null;
  clase?: 'muestreo' | 'tarea';
  labor_activa?: boolean;
  estadio?: string | null;
  ejecutada_at: string | null;
  ejecutada_por: string | null;
  resultado_json: ParResultado[] | null;
  observaciones: string | null;
}

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
function fmtFecha(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return `${DIAS[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type Filtro = 'pendientes' | 'ejecutados' | 'todos';
type FiltroClase = 'todos' | 'muestreo' | 'tarea';

export function MisMuestreosPage() {
  const empresaId = useEmpresaId();
  const [muestreos, setMuestreos] = useState<Muestreo[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('pendientes');
  const [filtroClase, setFiltroClase] = useState<FiltroClase>('todos');
  const [usuario, setUsuario] = useState('');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [registrar, setRegistrar] = useState<Muestreo | null>(null);
  const { online, pendientes: pendientesSync, sincronizando, sincronizar } = useOfflineSync();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUsuario((data.user?.user_metadata?.nombre as string) || (data.user?.email ?? '') || 'muestreador');
    });
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      // Traemos un rango amplio (desde hace 30 días) y filtramos en el cliente por estado.
      const desde = new Date(); desde.setDate(desde.getDate() - 30);
      const desdeISO = desde.toISOString().slice(0, 10);
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/programa?empresa_id=${empresaId}&desde=${desdeISO}`));
      const j = await res.json();
      setMuestreos(j.ok ? (j.programa || []) : []);
    } catch {
      setMuestreos([]);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!toast) return;
    // Los errores NO se auto-ocultan (para poder leerlos/copiarlos); los OK sí.
    if (!toast.ok) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  // Al terminar de sincronizar la cola (pendientes → 0), recargar para reflejar
  // los muestreos que pasaron a ejecutados en el servidor.
  const prevSync = useRef(0);
  useEffect(() => {
    if (prevSync.current > 0 && pendientesSync === 0) cargar();
    prevSync.current = pendientesSync;
  }, [pendientesSync, cargar]);

  const lista = useMemo(() => {
    const hoy = hoyISO();
    // Excluir inactivos (labor del catálogo desactivada) y filtrar por clase.
    let arr = muestreos
      .filter((m) => m.labor_activa !== false)
      .filter((m) => filtroClase === 'todos' || (m.clase || 'muestreo') === filtroClase);
    if (filtro === 'pendientes') arr = arr.filter((m) => m.estado === 'programada');
    else if (filtro === 'ejecutados') arr = arr.filter((m) => m.estado === 'ejecutada');
    // pendientes primero por fecha; ejecutados al final
    return [...arr].sort((a, b) => {
      const ap = a.estado === 'programada' ? 0 : 1;
      const bp = b.estado === 'programada' ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return a.fecha_programada.localeCompare(b.fecha_programada);
    }).map((m) => ({ ...m, vencido: m.estado === 'programada' && m.fecha_programada < hoy }));
  }, [muestreos, filtro, filtroClase]);

  // Contadores respetando el filtro de clase + excluyendo inactivos.
  const baseClase = muestreos
    .filter((m) => m.labor_activa !== false)
    .filter((m) => filtroClase === 'todos' || (m.clase || 'muestreo') === filtroClase);
  const pendientes = baseClase.filter((m) => m.estado === 'programada').length;
  const ejecutados = baseClase.filter((m) => m.estado === 'ejecutada').length;

  const reabrir = async (m: Muestreo) => {
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/programa/${m.id}/reabrir?empresa_id=${empresaId}`), { method: 'PATCH' });
      const j = await res.json();
      if (!j.ok) { setToast({ msg: j.error || 'Error', ok: false }); return; }
      setToast({ msg: 'Muestreo reabierto', ok: true });
      cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error de red', ok: false });
    }
  };

  const filtroBtn = (f: Filtro, label: string, n: number) => (
    <button onClick={() => setFiltro(f)}
      className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
      style={{
        background: filtro === f ? 'var(--emp-accent-bg)' : 'transparent',
        color: filtro === f ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
        border: filtro === f ? '1px solid var(--emp-accent)' : '1px solid var(--line)',
      }}>
      {label} · {n}
    </button>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'var(--surface)' }}>
      {/* Encabezado */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-4 flex-wrap" style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2">
          <ClipboardCheck size={20} style={{ color: 'var(--emp-accent-txt)' }} />
          <div>
            <h1 className="text-base font-bold" style={{ color: 'var(--ink)' }}>Mis Muestreos</h1>
            <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Registrá el resultado de los muestreos de campo</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Estado offline / cola de sincronización */}
          {!online && (
            <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold"
              style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}
              title="Sin conexión — los muestreos se guardan en el dispositivo y se sincronizan al recuperar señal">
              <WifiOff size={13} /> Sin conexión
            </span>
          )}
          {pendientesSync > 0 && (
            <button onClick={() => sincronizar()} disabled={sincronizando || !online}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold"
              style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)', opacity: (sincronizando || !online) ? 0.7 : 1 }}
              title={online ? 'Sincronizar ahora' : 'Se sincronizará al recuperar conexión'}>
              {sincronizando ? <Loader2 size={13} className="animate-spin" /> : <CloudUpload size={13} />}
              {pendientesSync} sin sincronizar
            </button>
          )}
          <select value={filtroClase} onChange={(e) => setFiltroClase(e.target.value as FiltroClase)}
            className="text-xs bg-transparent outline-none rounded px-2 py-1.5"
            style={{ color: 'var(--ink)', border: '1px solid var(--line)', backgroundColor: 'var(--surface-raised)', colorScheme: 'light dark' }}
            title="Filtrar por clase">
            <option value="todos" style={{ background: 'var(--surface-raised)', color: 'var(--ink)' }}>Todas las clases</option>
            <option value="muestreo" style={{ background: 'var(--surface-raised)', color: 'var(--ink)' }}>Solo Muestreos</option>
            <option value="tarea" style={{ background: 'var(--surface-raised)', color: 'var(--ink)' }}>Solo Tareas</option>
          </select>
          {filtroBtn('pendientes', 'Pendientes', pendientes)}
          {filtroBtn('ejecutados', 'Ejecutados', ejecutados)}
          {filtroBtn('todos', 'Todos', baseClase.length)}
          <button onClick={cargar} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium ml-2"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Recargar
          </button>
        </div>
      </div>

      {toast && (
        <div className="px-5 py-2 shrink-0 text-xs font-medium flex items-start gap-2"
          style={{ background: toast.ok ? 'var(--tone-success-bg)' : '#fff4e5', color: toast.ok ? 'var(--tone-success-text)' : '#a15c00', borderBottom: `1px solid ${toast.ok ? 'var(--tone-success-border)' : '#ffb74d'}` }}>
          <span className="flex-1 whitespace-pre-wrap break-words">{toast.msg}</span>
          {!toast.ok && (
            <button onClick={() => setToast(null)} className="shrink-0 font-bold px-1" style={{ color: '#a15c00' }}>✕</button>
          )}
        </div>
      )}

      {/* Lista de muestreos (cards) */}
      <div className="flex-1 overflow-auto p-5 space-y-2.5">
        {lista.length === 0 ? (
          <div className="py-12 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
            {loading ? 'Cargando…' : 'Sin muestreos para este filtro.'}
          </div>
        ) : lista.map((m) => {
          const ejecutada = m.estado === 'ejecutada';
          const sinFormato = !ejecutada && !m.tipo_muestreo;
          return (
            <div key={m.id} className="rounded-lg p-3 flex items-center gap-4"
              style={{
                background: 'var(--surface-raised)',
                border: `1px solid ${m.vencido ? '#f59e0b' : 'var(--line-dim)'}`,
                borderLeft: `3px solid ${ejecutada ? '#22c55e' : m.vencido ? '#f59e0b' : sinFormato ? 'var(--line)' : 'var(--emp-accent)'}`,
                opacity: sinFormato ? 0.6 : 1,
              }}>
              <div className="flex flex-col items-center justify-center shrink-0" style={{ width: '4.5rem' }}>
                <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>{m.semana}</span>
                <span className="text-sm font-mono font-bold" style={{ color: m.vencido ? '#f59e0b' : 'var(--ink)' }}>{fmtFecha(m.fecha_programada)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{m.labor_nombre}</div>
                <div className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                  Grupo <span className="font-mono">{m.grupo_forza}</span> · Lotes {m.lote_num}
                  {m.vencido && <span className="ml-2 font-semibold" style={{ color: '#f59e0b' }}>· vencido</span>}
                </div>
                {ejecutada && m.resultado_json && m.resultado_json.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {m.resultado_json.map((r, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                        style={{ background: 'var(--surface-deep)', color: 'var(--ink-muted)', border: '1px solid var(--line-dim)' }}>
                        {r.clave}: <b style={{ color: 'var(--ink)' }}>{r.valor}</b>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="shrink-0">
                {ejecutada ? (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#22c55e' }}>
                      <CheckCircle2 size={14} /> Ejecutada
                    </span>
                    <button onClick={() => setRegistrar(m)} title="Ver / editar"
                      className="px-2 py-1 rounded text-[11px]" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                      Ver
                    </button>
                    <button onClick={() => reabrir(m)} title="Reabrir"
                      className="p-1 rounded" style={{ border: '1px solid var(--line)', color: 'var(--ink-faint)' }}>
                      <RotateCcw size={13} />
                    </button>
                  </div>
                ) : m.tipo_muestreo ? (
                  <button onClick={() => setRegistrar(m)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-semibold"
                    style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)' }}>
                    <ClipboardCheck size={14} /> Registrar
                  </button>
                ) : (
                  // Labor sin formato de muestreo (tarea: aplicación, floración…) → atenuado
                  <span className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
                    style={{ border: '1px dashed var(--line)', color: 'var(--ink-faint)' }}
                    title="Esta labor es una tarea de campo, no tiene formato de muestreo">
                    Sin formato
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {registrar && (
        <ModalCapturaMuestreo
          muestreoId={registrar.id}
          empresaId={empresaId}
          usuario={usuario}
          onClose={() => setRegistrar(null)}
          onSaved={(msg) => { setToast({ msg, ok: true }); setRegistrar(null); cargar(); }}
          onError={(msg) => setToast({ msg, ok: false })}
        />
      )}
    </div>
  );
}
